import { getAccessToken, clearTokens } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/config/env';
import { createApiClient } from '@atsready/shared/utils';
import type { ApiClient } from '@atsready/shared/utils';

/**
 * Typed shape for API error responses.
 * Handles both `{ message: string }` (REST convention) and
 * `{ error: string }` (Hono's default error shape).
 */
export interface ApiErrorResponse {
  message?: string | undefined;
  error?: string | undefined;
}

/**
 * Type guard that narrows an unknown caught value to ApiErrorResponse.
 * Use in catch blocks to safely extract error details.
 */
export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  return typeof value === 'object' && value !== null;
}

export { API_BASE_URL };
export const API_URL = `${API_BASE_URL}/v1/admin`;

export function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export function handleUnauthorized(): never {
  clearTokens();
  window.location.href = '/login';
  throw new Error('Unauthorized');
}

// ============================================================================
// Shared client instance — wired to admin-specific auth
// ============================================================================

const _client: ApiClient = createApiClient({
  baseUrl: `${API_BASE_URL}/v1/admin`,
  getToken: () => getAccessToken(),
  clearSession: () => clearTokens(),
  onAuthFailure: () => {
    window.location.href = '/login';
  },
});

export async function request<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  return _client.request<T>(endpoint, options);
}
